import { useRef, useState } from 'react';
import { uploadLayoutImage } from '../../api/client';
import {
  formatScale,
  unitLabel,
  unitShort,
  UNIT_SYSTEM_OPTIONS,
} from '../../lib/calibration';
import { loadImageDimensions } from '../../lib/useLayoutImage';
import { useEditorStore } from '../../store/editorStore';
import type { UnitSystem } from '../../types/project';

// ------------------------------------------------------------
// Layout upload, unit system, and scale calibration controls.
// ------------------------------------------------------------

export default function LayoutPanel({
  readOnly = false,
  embedded = false,
}: {
  readOnly?: boolean;
  embedded?: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const projectId = useEditorStore((s) => s.projectId);
  const document = useEditorStore((s) => s.document);
  const calibrationMode = useEditorStore((s) => s.calibrationMode);
  const calibrationPoints = useEditorStore((s) => s.calibrationPoints);
  const calibrationDistanceInput = useEditorStore(
    (s) => s.calibrationDistanceInput,
  );
  const updateSettings = useEditorStore((s) => s.updateSettings);
  const setLayoutImage = useEditorStore((s) => s.setLayoutImage);
  const removeLayoutImage = useEditorStore((s) => s.removeLayoutImage);
  const startCalibration = useEditorStore((s) => s.startCalibration);
  const cancelCalibration = useEditorStore((s) => s.cancelCalibration);
  const setCalibrationDistanceInput = useEditorStore(
    (s) => s.setCalibrationDistanceInput,
  );
  const applyCalibration = useEditorStore((s) => s.applyCalibration);

  if (!document) return null;

  const unit: UnitSystem = document.settings?.unit_system ?? 'metric_m';
  const pixelsPerMeter = document.settings?.pixels_per_meter ?? 0;
  const hasLayout =
    Boolean(document.layout_image_id) || Boolean(document.layout_image_path);
  const layoutOpacity = document.settings?.layout_opacity ?? 0.85;
  const [p1, p2] = calibrationPoints;
  const canApplyCalibration = Boolean(p1 && p2 && calibrationDistanceInput);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || readOnly) return;

    if (!file.type.match(/^image\/(png|jpeg|webp)$/)) {
      setUploadError('Please choose a PNG, JPEG, or WebP image.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      const dims = await loadImageDimensions(file);
      const result = await uploadLayoutImage(file, projectId ?? '');
      setLayoutImage(result.id, dims.width, dims.height);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRecalibrate = () => {
    const existing = document.settings?.calibration_points;
    startCalibration(existing);
  };

  return (
    <div className={`space-y-3 p-4 ${embedded ? '' : 'border-t border-border'}`}>
      <div>
        <h3 className="panel-section-title mb-2">Layout image</h3>
        <p className="mb-2 text-[10px] leading-relaxed text-text-subtle">
          Upload a floor plan or site layout. Nodes are placed on top of the
          background.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => void handleFileChange(e)}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || readOnly}
          className="btn-secondary w-full py-2 text-xs disabled:opacity-50"
        >
          {uploading ? 'Uploading…' : hasLayout ? 'Replace layout' : 'Upload layout'}
        </button>

        {hasLayout && !readOnly && (
          <button
            type="button"
            onClick={removeLayoutImage}
            className="btn-ghost mt-1 w-full py-1.5 text-xs text-text-muted"
          >
            Remove layout
          </button>
        )}

        {uploadError && (
          <p className="mt-2 text-[10px] text-danger">{uploadError}</p>
        )}

        {hasLayout && (
          <label className="mt-3 block text-[11px] text-text-muted">
            <span className="mb-1 flex justify-between">
              <span>Background opacity</span>
              <span>{Math.round(layoutOpacity * 100)}%</span>
            </span>
            <input
              type="range"
              min={0.5}
              max={1}
              step={0.05}
              value={layoutOpacity}
              onChange={(e) =>
                !readOnly &&
                updateSettings({ layout_opacity: parseFloat(e.target.value) })
              }
              disabled={readOnly}
              className="w-full accent-accent"
            />
          </label>
        )}
      </div>

      <div>
        <h3 className="panel-section-title mb-2">Units &amp; scale</h3>

        <label className="mb-2 block text-[11px] text-text-muted">
          Unit system
          <select
            value={unit}
            onChange={(e) =>
              !readOnly &&
              updateSettings({ unit_system: e.target.value as UnitSystem })
            }
            disabled={readOnly}
            className="mt-1 w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text"
          >
            {UNIT_SYSTEM_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <p className="mb-2 text-[10px] text-text-subtle">
          Scale:{' '}
          <span className="text-text-muted">
            {formatScale(pixelsPerMeter, unit)}
          </span>
        </p>

        {!calibrationMode ? (
          <button
            type="button"
            onClick={() =>
              document.settings?.calibration_points
                ? handleRecalibrate()
                : startCalibration()
            }
            disabled={!hasLayout || readOnly}
            className="btn-secondary w-full py-2 text-xs disabled:opacity-40"
            title={
              hasLayout
                ? 'Measure two points on the layout to set scale'
                : 'Upload a layout image first'
            }
          >
            {document.settings?.calibration_points
              ? 'Re-calibrate scale'
              : 'Calibrate scale'}
          </button>
        ) : (
          <div className="space-y-2 rounded-lg border border-accent/30 bg-accent/10 p-2">
            <p className="text-[10px] leading-relaxed text-accent-on-dark">
              {!p1 && 'Step 1: Click the first point on the layout.'}
              {p1 && !p2 && 'Step 2: Click the second point on the layout.'}
              {p1 && p2 && 'Step 3: Enter the real distance between the points.'}
            </p>

            {p1 && p2 && (
              <label className="block text-[11px] text-text-muted">
                Distance ({unitShort(unit)})
                <input
                  type="number"
                  min={0}
                  step="any"
                  value={calibrationDistanceInput}
                  onChange={(e) => setCalibrationDistanceInput(e.target.value)}
                  placeholder={`e.g. 10 ${unitShort(unit)}`}
                  className="mt-1 w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text"
                />
                <span className="mt-0.5 block text-[10px] text-text-subtle">
                  Real-world distance in {unitLabel(unit)}
                </span>
              </label>
            )}

            <div className="flex gap-1">
              {p1 && p2 && (
                <button
                  type="button"
                  onClick={applyCalibration}
                  disabled={!canApplyCalibration}
                  className="btn-primary flex-1 py-1.5 text-xs disabled:opacity-40"
                >
                  Apply scale
                </button>
              )}
              <button
                type="button"
                onClick={cancelCalibration}
                className="btn-ghost flex-1 py-1.5 text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
